/**
 * Delivery Note React Hooks
 * Epic 63: Szallitolevol (Delivery Note) v2
 *
 * TanStack Query hooks for delivery note CRUD, summary, invoicing, settlement.
 */

import {
  createDeliveryNote,
  getDeliveryNote,
  getDeliveryNoteSummary,
  linkToInvoice,
  listDeliveryNotes,
  markSettled,
  type CreateDeliveryNoteInput,
  type DeliveryNote,
  type DeliveryNoteFilters,
  type DeliveryNoteSummary,
} from '@/api/delivery-notes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// QUERIES
// ============================================

export function useDeliveryNotes(filters?: DeliveryNoteFilters) {
  const query = useQuery({
    queryKey: ['delivery-notes', 'list', filters],
    queryFn: async () => {
      const res = await listDeliveryNotes(filters);
      return res.data;
    },
  });

  return {
    notes: (query.data ?? []) as DeliveryNote[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useDeliveryNote(id: string | undefined) {
  const query = useQuery({
    queryKey: ['delivery-notes', 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getDeliveryNote(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    note: query.data as DeliveryNote | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useDeliveryNoteSummary() {
  const query = useQuery({
    queryKey: ['delivery-notes', 'summary'],
    queryFn: async () => {
      const res = await getDeliveryNoteSummary();
      return res.data;
    },
  });

  return {
    summary: query.data as DeliveryNoteSummary | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// MUTATIONS
// ============================================

export function useCreateDeliveryNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeliveryNoteInput) => createDeliveryNote(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-notes'] });
    },
  });
}

export function useLinkToInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; noteIds: string[]; invoiceId: string }) =>
      linkToInvoice(data.id, data.noteIds, data.invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-notes'] });
    },
  });
}

export function useMarkSettled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markSettled(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-notes'] });
    },
  });
}
